import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Art } from '../components/Art';
import { TopBar } from '../components/ui';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { strip_code } from '../art/art';
import { VehicleArt } from '../art/Vehicle';
import { AuthHero } from '../components/AuthHero';
import { useLayout } from '../lib/layout';
import { useApp } from '../lib/app';
import { backspace, boxes, caretAt, emptyCode, isComplete, typeKeys, type CodeState } from '../lib/code';
import { phoneDisplay } from '../lib/format';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { Keypad } from './Keypad';
import { SIGNIN_KEY, type SigninState } from './Phone';

const RESEND_S = 30;

function readSignin(): SigninState | null {
  try {
    const s = JSON.parse(sessionStorage.getItem(SIGNIN_KEY) ?? 'null') as SigninState | null;
    return s && (s.phone || s.email) ? s : null;
  } catch {
    return null;
  }
}

/** Who the code went to, as the API wants it. */
const targetOf = (s: SigninState) => (s.email ? { email: s.email } : { phone: s.phone! });

export function Code() {
  const { api, onSignedIn } = useApp();
  const t = useT();
  const nav = useNavigate();
  const [st, setSt] = useState<SigninState | null>(readSignin);
  const [code, setCode] = useState<CodeState>(() => emptyCode(st?.codeLength ?? 6));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const tried = useRef('');
  const L = useLayout();

  useEffect(() => {
    if (!st) nav({ to: '/signin/phone' });
  }, [st, nav]);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Android Chrome can read the SMS code itself (WebOTP) once a real SMS provider sends "@zoomorides.com #123456"
  useEffect(() => {
    const otp = (navigator as Navigator & { credentials?: { get?: (o: unknown) => Promise<{ code?: string } | null> } }).credentials;
    if (!otp?.get || !('OTPCredential' in window)) return;
    const ac = new AbortController();
    otp
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((c) => c?.code && setCode((s) => typeKeys(s, c.code!)))
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  const verify = useCallback(
    async (digits: string) => {
      if (!st || busy || tried.current === digits) return;
      tried.current = digits;
      setBusy(true);
      setError(null);
      try {
        const r = await api.verifyOtp(targetOf(st), digits);
        sessionStorage.removeItem(SIGNIN_KEY);
        onSignedIn(r.rider);
        nav({ to: '/home', replace: true });
      } catch (e) {
        setError(friendly(e, t));
        setCode((s) => ({ ...s, digits: '' }));
        tried.current = '';
      } finally {
        setBusy(false);
      }
    },
    [api, st, busy, nav, onSignedIn, t],
  );

  useEffect(() => {
    if (isComplete(code)) void verify(code.digits);
  }, [code, verify]);

  const add = (d: string) => {
    setError(null);
    setCode((s) => typeKeys(s, d));
  };
  const back = () => setCode((s) => backspace(s));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) add(e.key);
      else if (e.key === 'Backspace') back();
    };
    const onPaste = (e: ClipboardEvent) => {
      const t = e.clipboardData?.getData('text') ?? '';
      if (t) setCode((s) => typeKeys(s, t));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('paste', onPaste);
    };
  }, []);

  const left = st ? Math.max(0, RESEND_S - Math.floor((now - st.sentAt) / 1000)) : 0;
  async function resend() {
    if (!st || left > 0) return;
    try {
      const r = await api.requestOtp(targetOf(st));
      const next = { ...st, devCode: import.meta.env.DEV ? r.devCode : undefined, sentAt: Date.now() };
      sessionStorage.setItem(SIGNIN_KEY, JSON.stringify(next));
      setSt(next);
      setError(null);
    } catch (e) {
      setError(friendly(e, t));
    }
  }

  const n = code.length;
  const wide = n <= 4;
  const cells = useMemo(() => boxes(code), [code]);
  const caret = caretAt(code);

  const header = <TopBar onBack={() => nav({ to: '/signin/phone' })} right={<span style={{ fontSize: 13, lineHeight: '16px', fontWeight: 700, color: 'var(--ink3)' }}>{t('code.step')}</span>} style={L.wide ? { padding: 0 } : undefined} />;
  const form = (
        <div style={{ flex: 'none', padding: L.wide ? 0 : '0 24px', marginTop: 'calc(24px * var(--vg))' }}>
          <h1 style={{ margin: 0, fontSize: L.wide ? 40 : 32, lineHeight: L.wide ? '44px' : '36px', fontWeight: 900, letterSpacing: '-0.03em' }}>{t('code.title')}</h1>
          <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: '24px', fontWeight: 500, color: 'var(--ink2)' }}>
            {st?.email ? t('code.sentEmail', { email: st.email }) : t('code.sent', { phone: st?.phone ? phoneDisplay(st.phone) : '' })}{' '}
            <a href="/signin/phone" onClick={(e) => (e.preventDefault(), nav({ to: '/signin/phone' }))} style={{ color: 'var(--ink)', fontWeight: 700 }}>
              {t('code.change')}
            </a>
          </p>

          <div role="group" aria-label={t('code.group', { n })} className={error ? 'zr-shake' : undefined} key={error ? `e-${error}` : "ok"} style={{ marginTop: 'calc(32px * var(--vg))', display: 'flex', gap: wide ? 14 : 8 }}>
            {cells.map((d, i) => {
              const focused = i === caret && !busy;
              return (
                <div
                  key={i}
                  aria-label={`${t('code.digit', { i: i + 1 })}${d ? `: ${d}` : ''}`}
                  style={{ position: 'relative', flex: wide ? '0 0 72px' : '1 1 0', height: wide ? 80 : 64, borderRadius: wide ? 20 : 16, background: 'var(--s1)', boxShadow: focused ? 'inset 0 0 0 1.5px var(--focus)' : error ? 'inset 0 0 0 1.5px var(--danger)' : 'inset 0 0 0 1.5px transparent', transition: 'box-shadow .18s var(--ease)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: wide ? 34 : 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                >
                  {d && <span className="zr-pop">{d}</span>}
                  {focused && !d && <span aria-hidden="true" style={{ position: 'absolute', left: '50%', marginLeft: -1, top: '30%', width: 2, height: '40%', borderRadius: 1, background: 'var(--focus)', animation: 'zr-blink 1s step-end infinite' }} />}
                </div>
              );
            })}
          </div>
          <p role={error ? 'alert' : undefined} style={{ margin: 'calc(16px * var(--vg)) 0 0', fontSize: 14, lineHeight: '20px', fontWeight: 500, color: error ? 'var(--danger)' : 'var(--ink2)' }}>
            {error ?? (busy ? t('code.signing') : t('code.auto'))}
          </p>

          <div style={{ marginTop: 'calc(26px * var(--vg))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            {left > 0 ? (
              <span style={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink3)', fontVariantNumeric: 'tabular-nums' }}>
                {t('code.resendIn', { s: String(left).padStart(2, '0') })}
              </span>
            ) : (
              <button type="button" onClick={() => void resend()} style={{ height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--ink)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {t('code.resend')}
              </button>
            )}
          </div>
        </div>
  );
  const pad = (
    <Keypad
              onDigit={add}
              onBack={back}
              top={
                L.wide ? undefined : st?.devCode ? (
                  <button
                    type="button"
                    aria-label={t('code.devFill', { code: st.devCode })}
                    onClick={() => setCode((s) => typeKeys(s, st.devCode!))}
                    className="zr-nopress"
                    style={{ width: '100%', height: 44, border: 0, background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--keyInk)', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 11, lineHeight: '13px', fontWeight: 500, opacity: 0.72 }}>{t('code.dev')}</span>
                    <span style={{ fontSize: 17, lineHeight: '20px', fontWeight: 700, letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums' }}>{st.devCode}</span>
                  </button>
                ) : (
                  <div style={{ height: 44 }} />
                )
              }
            />
  );

  if (L.wide)
    return (
      <AuthHero name="code" label="Code">
        {header}
        {form}
        {st?.devCode && (
          // development only (no SMS provider): the code the server made, one tap to type it
          <button type="button" aria-label={t('code.devFill', { code: st.devCode })} onClick={() => setCode((s) => typeKeys(s, st.devCode!))} className="zr-chip" style={{ marginTop: 16, background: 'var(--acSoft)', color: 'var(--acText)' }}>
            {t('code.dev')} · <span className="zr-num">{st.devCode}</span>
          </button>
        )}
        {pad}
      </AuthHero>
    );

  return (
    <Screen name="code" label="Code">
      <div className="zr-col zr-top">
        {header}
        {form}
        <div className="zr-fill" style={{ position: 'relative', overflow: 'hidden' }}>
          <Stage bottom={-2} height={108}>
            <Art piece={strip_code} />
            <VehicleArt type="AUTO" width={52} height={31} shadow={false} style={{ position: 'absolute', left: 236, top: 66 }} />
          </Stage>
        </div>

        {pad}
      </div>
    </Screen>
  );
}
